import React, {FC} from "react";
import {Checkbox, DatePicker, Form, Input, InputNumber, Select} from "antd";
import {buildDynamicAnswerRules} from "./formRules";
import {formatPublicLabel, normalizePublicFormFieldType} from "./publicRegistrationModel";
import {useRegistrationCopy} from "./registrationCopy";
import type {PublicFormField} from "./types";

const renderDynamicFieldInput = (field: PublicFormField) => {
    const fieldType = normalizePublicFormFieldType(field.type);
    const options = (field.options ?? [])
        .sort((a, b) => a.order - b.order)
        .map(option => ({
            value: option.value,
            label: formatPublicLabel(option.label, option.value)
        }));

    switch (fieldType) {
    case "TextArea":
        return <Input.TextArea rows={3}/>;
    case "Number":
        return <InputNumber style={{width: "100%"}}/>;
    case "Date":
        return <DatePicker style={{width: "100%"}}/>;
    case "Email":
        return <Input type="email"/>;
    case "Phone":
        return <Input type="tel"/>;
    case "Checkbox":
        return <Checkbox>{formatPublicLabel(field.label, field.code)}</Checkbox>;
    case "Select":
        return <Select options={options}/>;
    case "MultiSelect":
        return <Select mode="multiple" options={options}/>;
    case "Text":
    default:
        return <Input/>;
    }
};

// The event's additional questions, bound under `answers` (single) or `[row, "answers"]` (bulk).
export const DynamicAnswerFields: FC<{
    fields: PublicFormField[];
    namePrefix: Array<string | number>;
}> = ({fields, namePrefix}) => {
    const {labels} = useRegistrationCopy();

    return (
        <div className="public-registration-form-grid">
            {fields.map(field => {
                const isCheckbox = normalizePublicFormFieldType(field.type) === "Checkbox";
                return (
                    <Form.Item
                        key={field.code}
                        name={[...namePrefix, field.code]}
                        label={isCheckbox ? undefined : formatPublicLabel(field.label, field.code)}
                        valuePropName={isCheckbox ? "checked" : "value"}
                        rules={buildDynamicAnswerRules(field, labels)}
                    >
                        {renderDynamicFieldInput(field)}
                    </Form.Item>
                );
            })}
        </div>
    );
};
